/**
 * Governance-service (GS) envelope helpers for the Work Queues app.
 *
 * GS always responds with HTTP 200 even when the request failed — the real
 * outcome lives in the body's `statusCode` (+ `msgCode`). So callers cannot
 * trust the transport status; they must run the parsed body through
 * `validateApiResponse`, which throws on a non-2xx internal status and returns
 * a normalized `{ body, meta, ... }` on success.
 *
 * Pagination `meta` is inconsistent across GS endpoints: `knowledgebase/list`
 * returns snake_case (`total_items`, ...) while `change-request/list`,
 * `proposal/list`, `claim-suggestion/list` and `task/my-work/list` return
 * camelCase (`totalItems`, ...). `normalizePagedMeta` collapses both into one
 * camelCase shape.
 *
 * Mirrors the proven `apps/agents/src/shared/utils/apiResponse.js` pattern
 * (CT-5090). The reference implementation is the beta Governance Studio
 * "My Work" page — same GS backend, same envelope.
 */

/**
 * Extracts error information from a GS response body that may carry an internal
 * error (`statusCode`/`status_code` 4xx/5xx) while the HTTP status is 200.
 *
 * @param {Object} responseData - Parsed response body (the GS envelope).
 * @param {string} [defaultErrorMessage='An error occurred.'] - Fallback message.
 * @returns {{ message: string, statusCode: number, responseData: Object|null }|null}
 *   An error descriptor when the internal status is non-2xx, otherwise `null`.
 */
export const extractApiError = (responseData, defaultErrorMessage = 'An error occurred.') => {
  if (!responseData) {
    return { message: 'Invalid response from server', statusCode: 500, responseData: null };
  }

  const statusCode = responseData.status_code ?? responseData.statusCode ?? 200;
  const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];

  if (statusCode >= 200 && statusCode < 300) return null;

  const errorMessage =
    msgCode.length > 0
      ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
      : defaultErrorMessage;

  return { message: errorMessage, statusCode, responseData };
};

/**
 * Validates a GS response body and returns a normalized object, or throws.
 *
 * @param {Object} responseData - Parsed GS envelope
 *   (`{ statusCode, msgCode:[{code,description}], requestId, body, meta }`).
 * @param {string} [defaultErrorMessage='An error occurred.'] - Fallback message.
 * @returns {{ status_code: number, msg_code: Array, body: unknown, meta: Object|null, request_id: string|null }}
 *   Normalized snake_case shape, matching `apps/agents/.../apiResponse.js` for
 *   cross-app consistency. Consumers read `body`/`meta`.
 * @throws {Error} When the internal status is not 2xx. The thrown error carries
 *   `error.response = { data, status }` for downstream handling.
 */
export const validateApiResponse = (responseData, defaultErrorMessage = 'An error occurred.') => {
  const error = extractApiError(responseData, defaultErrorMessage);
  if (error) {
    const apiError = new Error(error.message);
    apiError.response = { data: error.responseData, status: error.statusCode };
    throw apiError;
  }

  return {
    status_code: responseData.status_code ?? responseData.statusCode ?? 200,
    msg_code: responseData.msg_code ?? responseData.msgCode ?? [],
    body: responseData.body ?? null,
    meta: responseData.meta ?? null,
    request_id: responseData.request_id ?? responseData.requestId ?? null,
  };
};

/**
 * Normalizes a GS pagination `meta` object into a single camelCase shape,
 * accepting either the snake_case (`knowledgebase/list`) or the camelCase
 * (every other list endpoint) convention.
 *
 * @param {Object|null|undefined} meta - Raw `meta` from a GS list response.
 * @returns {{ totalItems: number, totalPages: number, currentPage: number, perPage: number, hasNext: boolean, hasPrevious: boolean, nextCursor: string|null }|null}
 *   Normalized meta, or `null` when no meta was provided.
 */
export const normalizePagedMeta = (meta) => {
  if (!meta || typeof meta !== 'object') return null;

  return {
    totalItems: meta.totalItems ?? meta.total_items ?? 0,
    totalPages: meta.totalPages ?? meta.total_pages ?? 0,
    currentPage: meta.currentPage ?? meta.current_page ?? 1,
    perPage: meta.perPage ?? meta.per_page ?? 0,
    hasNext: meta.hasNext ?? meta.has_next ?? false,
    hasPrevious: meta.hasPrevious ?? meta.has_previous ?? false,
    nextCursor: meta.nextCursor ?? meta.next_cursor ?? null,
  };
};
